import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Upload,
  Filter,
  Search,
  MoreHorizontal,
  Eye,
  CheckCircle,
  Clock,
  AlertTriangle,
  Download,
  Plus,
  Loader2,
  ExternalLink,
  Pencil,
  Trash2,
  Mail,
  RefreshCw,
  Tag,
  Building2,
  ChevronLeft,
  ChevronRight,
  Layers,
  Ban,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DashboardLayout } from '../components/DashboardLayout';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { useSearchParams } from 'react-router-dom';
import { invoicesAPI, matchingAPI, integrationsAPI, brosAPI, exchangeRatesAPI, invoiceTaxonomyAPI, invoiceCountryAPI, gmailEntityAssignmentAPI, API_URL } from '../lib/api';
import { TaxonomyChipRow } from '../components/invoices/TaxonomyChipRow';
import { computeInvoiceStats, isOverdue, reviewStatusBadge, duplicateNotificationBadge } from '../lib/invoiceListDerive';
import { SUPPLIER_TAXONOMY_CATEGORIES, CLIENT_TAXONOMY_CATEGORIES } from '../lib/invoiceTaxonomy';
import { ReviewQueueContent } from './ReviewQueuePage';
import { InvoicePdfButton } from '../components/InvoicePdfButton';
import { DateScopeControl } from '../components/DateScopeControl';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { getErrorMessage } from '../lib/errors';
import { getConversionRate, convertAmount } from '../lib/currencyConvert';
import {
  CURRENCY_SYMBOLS,
  jurisdictions, jurisdictionsByKey,
  ENTITY_TYPE_DISPLAY_NAME, getJurisdictionKey, getEntityDotColor,
} from '../lib/entities';
import { format } from 'date-fns';

const sourceConfig = {
  gmail: { label: 'Gmail', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  pennylane: { label: 'Pennylane', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  xero: { label: 'Xero', className: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  bros: { label: 'BROS', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  chief_of_staff: { label: 'CoS', className: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  management_statement: { label: 'Mgmt statement', className: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  manual: { label: 'Manual', className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
};

const paymentStatusConfig = {
  unpaid: { label: 'Unpaid', icon: Clock, className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  partially_paid: { label: 'Partial', icon: Clock, className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
  paid: { label: 'Paid', icon: CheckCircle, className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  // Voided in the issuing system (BR OS): nothing is due, and it is not
  // "Unpaid" -- see backend/services/bros_void_sync.py.
  void: { label: 'Void', icon: Ban, className: 'bg-slate-100 text-slate-500 line-through dark:bg-slate-800 dark:text-slate-400' },
  // Chief of Staff sends its own workflow vocabulary. Historic rows still
  // carry it in payment_status; the sync now maps it (services/
  // chief_of_staff.py::map_cos_payment_status), but an unmapped value must
  // never silently fall back to "Unpaid" on a receipt whose money is
  // already gone. [C17, 2026-09-03]
  confirmed: { label: 'Paid', icon: CheckCircle, className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  awaiting_confirmation: { label: 'Paid', icon: CheckCircle, className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
};

const reconStatusConfig = {
  reconciled: { label: 'Reconciled', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  partial: { label: 'Partial', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  not_reconciled: { label: 'Not reconciled', className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  // BR OS's own "paid" boolean carries no transaction evidence Finance
  // Agent can verify (see backend routes/invoices.py's recon_status
  // comment) -- "Not reconciled" reads as "no payment evidence at all",
  // which is wrong here. Distinct from "not_reconciled" everywhere else.
  paid_unreconciled_bros: { label: 'Paid (BR OS) - unreconciled', className: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
  // Gmail-ingested tool-subscription receipt (e.g. Anthropic) whose own
  // text already said "paid" but services/receipt_paid_reconciliation.py
  // has not yet corroborated it against the bank feed. The
  // paid_unreconciled_bros analogue for non-BR-OS receipts.
  paid_pending_verification: { label: 'Paid - pending verification', className: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' },
};

// Backwards-compatible lookup: if recon_status is present use it, otherwise fall back to boolean.
const reconciledConfig = {
  true: reconStatusConfig.reconciled,
  false: reconStatusConfig.not_reconciled,
};

// Default date scope (CLAUDE.md Commit 3.1): "From 2026" onward, not all
// time -- an operator can widen to "All time" or a custom range via
// DateScopeControl. Server-side date_from/date_to (services/invoice_query.py),
// never a client-side row filter, so X-Total-Count and the taxonomy chips
// stay in parity with the table.
const DEFAULT_DATE_SCOPE = { from: '2026-01-01' };

// Country filter: derived server-side from the doc's entity via ENTITY_DEFS
// (config.py resolve_entity_country/build_country_filter), never a
// name-string match. Shown on both tabs.
const COUNTRY_CATEGORIES = [
  { key: 'UK', label: 'UK' },
  { key: 'France', label: 'France' },
  { key: 'Unassigned', label: 'Unassigned' },
];

const formatCurrency = (value, currency = 'GBP') => {
  const symbols = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };
  return `${symbols[currency] || currency}${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const safeFormatDate = (dateValue, fmt = 'dd MMM yyyy') => {
  if (!dateValue) return '—';
  try {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '—';
    return format(d, fmt);
  } catch {
    return '—';
  }
};

function InvoiceDetailModal({ invoice, isOpen, onClose, pdfMatchInfo }) {
  if (!invoice) return null;

  const payCfg = paymentStatusConfig[invoice.payment_status] ?? paymentStatusConfig.unpaid;
  const reconCfg = reconStatusConfig[invoice.recon_status] ?? reconciledConfig[invoice.reconciled] ?? reconStatusConfig.not_reconciled;
  const PayIcon = payCfg.icon;
  const overdue = isOverdue(invoice);
  const supplierClient = invoice.supplier_client_name || 'Unknown';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Invoice {invoice.invoice_number}
          </DialogTitle>
          <DialogDescription>
            {invoice.type === 'inbound' ? 'Supplier Invoice' : 'Client Invoice'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">
                {invoice.type === 'inbound' ? 'Supplier' : 'Client'}
              </p>
              <p className="font-medium">{supplierClient}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Status</p>
              <div className="flex flex-wrap gap-1.5">
                <Badge className={payCfg.className}>
                  <PayIcon className="h-3 w-3 mr-1" />
                  {payCfg.label}
                </Badge>
                <Badge variant="secondary" className={reconCfg.className}>
                  {reconCfg.label}
                </Badge>
                {overdue && (
                  <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Overdue
                  </Badge>
                )}
                {reviewStatusBadge(invoice) && (
                  <Badge className={reviewStatusBadge(invoice).className}>
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {reviewStatusBadge(invoice).label}
                  </Badge>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Invoice Date</p>
              <p className="font-medium">{safeFormatDate(invoice.date)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Due Date</p>
              <p className="font-medium">{safeFormatDate(invoice.due_date)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Amount</p>
              <p className="text-xl font-bold font-numeric">{formatCurrency(invoice.amount, invoice.currency)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">VAT ({invoice.vat_rate}%)</p>
              <p className="font-medium">{formatCurrency(invoice.vat_amount, invoice.currency)}</p>
            </div>
          </div>

          {invoice.line_items && invoice.line_items.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Line Items</p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3">Description</th>
                      <th className="text-right p-3">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.line_items.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-3">{item.description}</td>
                        <td className="p-3 text-right">{formatCurrency(item.amount, invoice.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <InvoicePdfButton invoice={invoice} pdfMatchInfo={pdfMatchInfo} variant="button" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

var INVOICES_SECTION_TABS = [
  { key: 'invoices', label: 'Invoices' },
  { key: 'review', label: 'Review Queue' },
];

export function InvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section') ?? 'invoices';

  const handleSectionChange = useCallback(function (key) {
    if (key === 'invoices') {
      setSearchParams({});
    } else {
      setSearchParams({ section: key });
    }
  }, [setSearchParams]);

  const { selectedEntity, entities } = useEntityStore();
  const [invoices, setInvoices] = useState([]);
  // ?search= seeds the free-text filter below for a plain manual search
  // deep link. The search string is sent to the server (see fetchInvoices)
  // and applied before skip/limit, so it matches across the FULL filtered
  // set, not just whatever page happens to already be loaded. The Bank
  // Transactions "Reconciled By" column no longer uses this -- it
  // deep-links via ?invoice_id= instead (see the effect below), which
  // opens that specific invoice's own detail view regardless of pagination.
  const [confirmedMatches, setConfirmedMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [isSyncingReceipts, setIsSyncingReceipts] = useState(false);
  const [isImportingBros, setIsImportingBros] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  // Debounced echo of searchTerm -- this is what actually drives the fetch
  // (params/deps below), so keystrokes don't each fire their own request.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [entityFilter, setEntityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('outbound');
  const [invoiceTab, setInvoiceTab] = useState('client');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [reconFilter, setReconFilter] = useState('all');
  const [taxonomyFilter, setTaxonomyFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [dateScope, setDateScope] = useState(DEFAULT_DATE_SCOPE);
  const [taxonomyCounts, setTaxonomyCounts] = useState({});
  const [countryCounts, setCountryCounts] = useState({});
  const [totalInvoiceCount, setTotalInvoiceCount] = useState(0);
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(1);
  // Payment Date column sort -- server-side (GET /invoices sort_by/sort_dir,
  // whitelisted in routes/invoices.py::SORTABLE_FIELDS). No other column has
  // this yet; deliberately scoped to just this one, not a general feature.
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [pdfMatchMap, setPdfMatchMap] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [descriptionInvoice, setDescriptionInvoice] = useState(null);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [fxRates, setFxRates] = useState(null);
  const [assignableEntities, setAssignableEntities] = useState([]);
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);

  const fetchInvoices = async () => {
    try {
      // Resolve entity selection to the type code used in invoice documents.
      // Sidebar selectedEntity is a UUID; invoices store type codes (eci, br_ltd, etc.).
      let effectiveEntityTypeCode;
      if (entityFilter !== 'all') {
        // Page-level entity filter uses type codes or entity names directly
        effectiveEntityTypeCode = entityFilter;
      } else if (selectedEntity !== 'all') {
        // Sidebar entity is a UUID — pass it directly
        effectiveEntityTypeCode = selectedEntity;
      }
      // Real server-side pagination (page/page_size) replaces the old flat
      // limit:500 cap -- a filter matching thousands of docs (e.g. a
      // taxonomy chip) is now browsable to the end via the pager below,
      // instead of always fetching the same first 500 and reporting the
      // rest as unreachable.
      const params = { page, page_size: PAGE_SIZE };
      if (effectiveEntityTypeCode) params.entity_id = effectiveEntityTypeCode;
      if (statusFilter !== 'all') params.payment_status = statusFilter;
      if (directionFilter !== 'all') params.direction = directionFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (docTypeFilter !== 'all') params.document_type = docTypeFilter;
      if (taxonomyFilter !== 'all') params.taxonomy = taxonomyFilter;
      if (countryFilter !== 'all') params.country = countryFilter;
      if (dateScope?.from) params.date_from = dateScope.from;
      if (dateScope?.to) params.date_to = dateScope.to;
      if (debouncedSearchTerm.trim()) params.search = debouncedSearchTerm.trim();
      if (reconFilter !== 'all') params.recon_status = reconFilter;
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
      }

      // Two independent fetches, not a single Promise.all: confirmedMatches
      // only feeds the Revert-button eligibility set below
      // (confirmedInvoiceIds/groupIdByInvoiceId), never the core invoices
      // list. Promise.all's fail-together semantics used to mean a
      // transient failure of THIS unrelated call (e.g. GET /matches?
      // status=confirmed briefly 500ing) blanked the entire table even
      // though the invoices fetch itself had already succeeded -- exactly
      // what shipped at HEAD with zero rows rendering against a live 84-row
      // dataset. Both requests still fire concurrently (matchesPromise
      // starts immediately, before invoicesPromise is awaited); only the
      // matches one is caught inline so its failure can never reject the
      // invoices path, and degrades to "no eligibility data yet" rather
      // than an empty page.
      const invoicesPromise = invoicesAPI.getAll(params);
      const matchesPromise = matchingAPI.getAll('confirmed').catch((error) => {
        console.error('Failed to fetch confirmed matches (Revert-button eligibility may be stale):', error);
        return { data: [] };
      });

      const invoicesRes = await invoicesPromise;
      setInvoices(invoicesRes.data);
      setTotalInvoiceCount(Number(invoicesRes.headers?.['x-total-count']) || invoicesRes.data.length);

      const matchesRes = await matchesPromise;
      setConfirmedMatches(matchesRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTaxonomyCounts = async () => {
    try {
      const params = {};
      if (entityFilter !== 'all') params.entity_id = entityFilter;
      if (statusFilter !== 'all') params.payment_status = statusFilter;
      if (directionFilter !== 'all') params.direction = directionFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (docTypeFilter !== 'all') params.document_type = docTypeFilter;
      if (countryFilter !== 'all') params.country = countryFilter;
      if (dateScope?.from) params.date_from = dateScope.from;
      if (dateScope?.to) params.date_to = dateScope.to;
      if (debouncedSearchTerm.trim()) params.search = debouncedSearchTerm.trim();
      if (reconFilter !== 'all') params.recon_status = reconFilter;
      const res = await invoiceTaxonomyAPI.getCounts(params);
      setTaxonomyCounts(res.data?.counts || {});
    } catch (error) {
      // Non-critical: chip badges just show 0 if this fails.
    }
  };

  const fetchCountryCounts = async () => {
    try {
      const params = {};
      if (entityFilter !== 'all') params.entity_id = entityFilter;
      if (statusFilter !== 'all') params.payment_status = statusFilter;
      if (directionFilter !== 'all') params.direction = directionFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (docTypeFilter !== 'all') params.document_type = docTypeFilter;
      if (taxonomyFilter !== 'all') params.taxonomy = taxonomyFilter;
      if (dateScope?.from) params.date_from = dateScope.from;
      if (dateScope?.to) params.date_to = dateScope.to;
      if (debouncedSearchTerm.trim()) params.search = debouncedSearchTerm.trim();
      if (reconFilter !== 'all') params.recon_status = reconFilter;
      const res = await invoiceCountryAPI.getCounts(params);
      setCountryCounts(res.data?.counts || {});
    } catch (error) {
      // Non-critical: chip badges just show 0 if this fails.
    }
  };

  const handleSetCategory = async (invoice, taxonomy, label) => {
    const counterpartyName = invoice.supplier_client_name;
    if (!window.confirm(
      `Set "${counterpartyName}" to ${label}? This re-tags every invoice from this counterparty, not just this one.`
    )) return;
    try {
      const res = await invoiceTaxonomyAPI.setCategory({
        counterparty_name: counterpartyName,
        taxonomy,
        direction: invoice.type === 'outbound' ? 'outbound' : 'inbound',
      });
      toast.success(`Re-tagged ${res.data.updated} of ${res.data.total_docs} invoice(s) from ${counterpartyName}`);
      fetchInvoices();
      fetchTaxonomyCounts();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSetEntity = async (invoice, entityId, entityName) => {
    const applyToCounterparty = window.confirm(
      `Set entity to ${entityName} for this document?\n\nClick OK to also map "${invoice.supplier_client_name}" → ${entityName} ` +
      `so future Gmail invoices from this counterparty resolve automatically. Click Cancel to set this document only.`
    );
    try {
      const res = await gmailEntityAssignmentAPI.setEntity(invoice.id, entityId, applyToCounterparty);
      const extra = res.data?.counterparty_restamped ? ` (+${res.data.counterparty_restamped} other doc(s) from this counterparty)` : '';
      toast.success(`Entity set to ${entityName}${extra}`);
      fetchInvoices();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const fetchGoogleAccounts = async () => {
    try {
      const res = await integrationsAPI.google.getAccounts();
      setGoogleAccounts(res.data?.accounts || []);
    } catch (error) {
      // Silently fail - user may not have Google integration
    }
  };

  const fetchFxRates = async () => {
    try {
      const res = await exchangeRatesAPI.getLatest();
      setFxRates(res.data);
    } catch (error) {
      // Aggregate cards fall back to a loading state until rates arrive;
      // non-critical, no per-invoice figure depends on this.
    }
  };

  const handleScanGmail = async (forceRescan = false) => {
    const connectedAccounts = googleAccounts.filter(a => a.connected);
    if (connectedAccounts.length === 0) {
      toast.error('No Gmail accounts connected. Go to Integrations to connect.');
      return;
    }

    setIsScanning(true);
    try {
      const res = await integrationsAPI.google.processAllInvoices({ forceRescan });
      const d = res.data;
      // Log per-account detail to console for debugging
      if (d.results) {
        console.log('Gmail scan results:', d.results.map(r =>
          `${r.account_email || r.google_email || r.account_id}: ${r.messages_found ?? '?'} found, ${r.processed ?? 0} processed, ${r.errors ?? 0} errors${r.error ? ` (${r.error})` : ''}`
        ));
      }
      if (d.accounts_failed && d.accounts_failed.length > 0) {
        toast.error(`Failed to scan: ${d.accounts_failed.join(', ')}. Check connection in Integrations.`);
      }
      if (d.total_processed > 0) {
        toast.success(`Scanned ${d.accounts_succeeded} account(s): ${d.total_processed} invoices processed`);
        fetchInvoices();
      } else if (d.total_errors > 0 && (!d.accounts_failed || d.accounts_failed.length === 0)) {
        const breakdown = (d.results || []).map(r =>
          `${(r.account_email || r.google_email || '').split('@')[0]}: ${r.messages_found ?? '?'} found`
        ).join(', ');
        toast.error(`Scan errors (${breakdown})`);
      } else if (!d.accounts_failed || d.accounts_failed.length === 0) {
        const breakdown = (d.results || []).map(r =>
          `${(r.account_email || r.google_email || '').split('@')[0]}: ${r.messages_found ?? 0} found`
        ).join(', ');
        toast.info(`No new invoices (${breakdown})${!forceRescan ? '. Try "Force rescan all".' : '.'}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsScanning(false);
    }
  };

  const handleSyncReceipts = async () => {
    setIsSyncingReceipts(true);
    try {
      const res = await invoicesAPI.syncChiefOfStaff();
      const { created, updated, total } = res.data;
      if (created > 0 || updated > 0) {
        toast.success(`Synced ${total} receipts (${created} new, ${updated} updated)`);
        fetchInvoices();
      } else if (total === 0) {
        toast.info('No receipts found in Chief of Staff');
      } else {
        toast.info('Receipts already up to date');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSyncingReceipts(false);
    }
  };

  const handleImportBros = async () => {
    setIsImportingBros(true);
    try {
      const res = await brosAPI.importInvoices();
      const { created, updated, total } = res.data;
      if (created > 0 || updated > 0) {
        toast.success(`Imported ${total} BROS invoices (${created} new, ${updated} updated)`);
        fetchInvoices();
      } else if (total === 0) {
        toast.info('No invoices found in BROS');
      } else {
        toast.info('BROS invoices already up to date');
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsImportingBros(false);
    }
  };

  // Debounce the search input -- 350ms after typing stops, echo it into
  // debouncedSearchTerm, which is what actually drives the server fetch
  // below (a request per keystroke would flood the list/taxonomy/country
  // endpoints and thrash the page's own loading state).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchTerm(searchTerm), 350);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Reset to page 1 whenever a filter narrows/widens the result set --
  // otherwise a stale page number from a broader filter can land past the
  // end of a narrower one's total and render an empty page.
  useEffect(() => {
    setPage(1);
  }, [selectedEntity, entityFilter, statusFilter, directionFilter, sourceFilter, docTypeFilter, taxonomyFilter, countryFilter, dateScope, debouncedSearchTerm, reconFilter]);

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all') {
      setIsLoading(true);
      fetchInvoices();
    }
  }, [selectedEntity, entities, entityFilter, statusFilter, directionFilter, sourceFilter, docTypeFilter, taxonomyFilter, countryFilter, dateScope, debouncedSearchTerm, reconFilter, page, sortBy, sortDir]);

  const toggleSort = (field) => {
    setPage(1);
    if (sortBy !== field) {
      setSortBy(field);
      setSortDir('desc');
    } else {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    }
  };

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all') {
      fetchTaxonomyCounts();
    }
    // Deliberately excludes taxonomyFilter: chip badges must show every
    // bucket's size under the OTHER active filters, not just the selected one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, entities, entityFilter, statusFilter, directionFilter, sourceFilter, docTypeFilter, countryFilter, dateScope, debouncedSearchTerm, reconFilter]);

  useEffect(() => {
    if (entities.length > 0 || selectedEntity === 'all') {
      fetchCountryCounts();
    }
    // Deliberately excludes countryFilter: chip badges must show every
    // bucket's size under the OTHER active filters, not just the selected one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, entities, entityFilter, statusFilter, directionFilter, sourceFilter, docTypeFilter, taxonomyFilter, dateScope, debouncedSearchTerm, reconFilter]);

  useEffect(() => {
    fetchGoogleAccounts();
    fetchFxRates();
    gmailEntityAssignmentAPI.getEntities()
      .then((res) => setAssignableEntities(res.data?.entities || []))
      .catch(() => {});
  }, []);

  // Pre-populate PDF match status for Bros invoices so the button
  // can show disabled with reason instead of a broken link.
  useEffect(() => {
    const brosIds = invoices
      .filter((inv) => inv.source === 'bros' && inv.id)
      .map((inv) => inv.id);
    if (brosIds.length === 0) {
      setPdfMatchMap({});
      return;
    }
    invoicesAPI.pdfMatchBatch(brosIds).then((res) => {
      setPdfMatchMap(res.data?.matches || {});
    }).catch(() => {
      // Non-critical; buttons fall back to optimistic
    });
  }, [invoices]);

  // Deep-link from the Bank Transactions "Reconciled By" column
  // (lib/reconciliationArtifact.js): ?invoice_id= opens THAT invoice's own
  // detail view, regardless of pagination/filters -- never the bare list.
  // Reuses the already-loaded page when the invoice happens to be on it;
  // otherwise fetches it directly (GET /invoices/{id}) since the list
  // fetch above is capped/filtered and may never include it.
  useEffect(() => {
    const invoiceId = searchParams.get('invoice_id');
    if (!invoiceId) return;
    const alreadyLoaded = invoices.find((inv) => inv.id === invoiceId);
    if (alreadyLoaded) {
      setSelectedInvoice(alreadyLoaded);
      return;
    }
    invoicesAPI.getById(invoiceId)
      .then((res) => setSelectedInvoice(res.data))
      .catch((error) => toast.error(getErrorMessage(error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('invoice_id')]);

  // Every filter (including search and reconciliation status) is applied
  // server-side now (see fetchInvoices) -- `invoices` already IS the
  // filtered, paginated result. This just preserves the table's existing
  // by-invoice-date-desc display order within that page; it is a sort,
  // never a filter (rows are never dropped here).
  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
  }, [invoices]);

  const openEditDialog = (invoice) => {
    setEditingInvoice(invoice);
    setEditForm({
      supplier_client_name: invoice.supplier_client_name || '',
      entity_name: invoice.entity_name || '',
      entity_id: invoice.entity_id || '',
      payer_entity_name: invoice.payer_entity_name || '',
      payer_entity_id: invoice.payer_entity_id || '',
      invoice_number: invoice.invoice_number || '',
      description: invoice.description || '',
      type: invoice.type || 'inbound',
      amount: invoice.amount ?? '',
      amount_excl_vat: invoice.amount_excl_vat ?? '',
      vat_amount: invoice.vat_amount ?? '',
      vat_rate: invoice.vat_rate ?? '',
      currency: invoice.currency || 'GBP',
      country: invoice.country || '',
      issue_date: invoice.issue_date || invoice.date || '',
      due_date: invoice.due_date || '',
      payment_status: invoice.payment_status || 'unpaid',
      category: invoice.category || 'supplier',
      document_type: invoice.document_type || 'invoice',
    });
  };

  const handleSaveInvoice = async () => {
    if (!editingInvoice || isSaving) return;
    // Validate required fields
    if (!editForm.supplier_client_name?.trim()) {
      toast.error('Vendor/Supplier name is required');
      return;
    }
    if (!editForm.amount && editForm.amount !== 0) {
      toast.error('Amount is required');
      return;
    }
    setIsSaving(true);
    try {
      await invoicesAPI.update(editingInvoice.id, editForm);
      toast.success('Invoice updated');
      setEditingInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (invoiceId) => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await invoicesAPI.delete(invoiceId);
      toast.success('Invoice deleted');
      setDeletingInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  // Cards derive from the SAME sortedInvoices the table renders -- they
  // cannot structurally diverge under any chip/search combination.
  const stats = computeInvoiceStats(sortedInvoices, isOverdue);

  // Rates fetched from the live /fx/rates endpoint (canonical currencyConvert.js
  // path), not the static FX_TO_EUR approximation table. /fx/rates?base=GBP
  // returns GBP->currency rates (e.g. EUR: 1.17, meaning 1 GBP = 1.17 EUR);
  // currencyConvert.js's raw-shape convention expects the inverse, currency->GBP
  // (1 unit of currency = N GBP), so invert each rate before use.
  const fxRatesMap = fxRates?.rates
    ? Object.fromEntries(
        Object.entries(fxRates.rates).map(([cur, rate]) => [cur, rate ? 1 / rate : rate])
      )
    : null;
  const ratesReady = !!fxRatesMap && getConversionRate('EUR', 'GBP', fxRatesMap) !== 1;

  const convertAmountsToTarget = (amounts, targetCurrency, ratesMap) =>
    Object.entries(amounts).reduce(
      (sum, [cur, amt]) => sum + convertAmount(amt, cur, targetCurrency, ratesMap),
      0
    );

  const renderAmounts = (amounts, colorClass = '') => {
    if (Object.keys(amounts).length === 0) return <span className="text-muted-foreground">—</span>;
    if (!ratesReady) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    const total = convertAmountsToTarget(amounts, displayCurrency, fxRatesMap);
    return (
      <p className={`text-xl font-bold font-numeric leading-tight ${colorClass}`}>
        {formatCurrency(total, displayCurrency)}
      </p>
    );
  };

  // Aggregate totals by jurisdiction and entity (name-based jurisdiction mapping)
  const { byJurisdiction, byEntityBreakdown } = useMemo(() => {
    const jurs = {};
    const ents = {};

    for (const inv of invoices) {
      const entityType = inv.entity_id; // type code: "eci", "br_ltd", "personal"
      const rawName = inv.entity_name || entityType || 'Unknown';
      const entityName = ENTITY_TYPE_DISPLAY_NAME[rawName] || rawName;
      if (!entityType && !inv.entity_name) continue;

      const currency = inv.currency || 'GBP';
      const amount = inv.amount || 0;

      const jurKey = getJurisdictionKey(entityName, entityType) || getJurisdictionKey(rawName, entityType);
      if (!jurKey) continue;

      // Entity bucket
      const entKey = `${jurKey}::${entityName}`;
      if (!ents[entKey]) ents[entKey] = { entity: { id: entKey, name: entityName, type: entityType, jurisdiction: jurKey }, amounts: {}, count: 0 };
      ents[entKey].amounts[currency] = (ents[entKey].amounts[currency] || 0) + amount;
      ents[entKey].count++;

      // Jurisdiction bucket
      if (!jurs[jurKey]) jurs[jurKey] = { amounts: {}, count: 0 };
      jurs[jurKey].amounts[currency] = (jurs[jurKey].amounts[currency] || 0) + amount;
      jurs[jurKey].count++;
    }

    return { byJurisdiction: jurs, byEntityBreakdown: ents };
  }, [invoices]);

  // Invoices with a live confirmed match -- only these can be reverted.
  const confirmedInvoiceIds = useMemo(
    () => new Set(confirmedMatches.map((m) => m.invoice_id)),
    [confirmedMatches]
  );

  // A group-matched invoice cannot be reverted alone (the backend refuses
  // it -- a partial release is not a state, see routes/matching.py::
  // revert_confirmed_match's group guard). Look up its match_group_id so
  // the revert prompt can route to PUT /matches/group/{id}/revert instead.
  const groupIdByInvoiceId = useMemo(() => {
    const map = {};
    for (const m of confirmedMatches) {
      if (m.match_group_id) map[m.invoice_id] = m.match_group_id;
    }
    return map;
  }, [confirmedMatches]);

  const handleRevertMatch = async (invoice) => {
    const groupId = groupIdByInvoiceId[invoice.id];
    if (groupId) {
      const groupInvoiceCount = confirmedMatches.filter((m) => m.match_group_id === groupId).length;
      if (!window.confirm(
        `Invoice ${invoice.invoice_number} is part of a group match settling ${groupInvoiceCount} invoices with one ` +
        `transaction. Revert the WHOLE group? This restores all ${groupInvoiceCount} invoices to Unpaid.`
      )) return;
      try {
        await matchingAPI.revertGroup(groupId);
        toast.success(`Reverted group match (${groupInvoiceCount} invoices)`);
        fetchInvoices();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
      return;
    }
    if (!window.confirm(`Revert the confirmed match for invoice ${invoice.invoice_number}? This restores it to Unpaid.`)) return;
    try {
      await matchingAPI.revertByInvoice(invoice.id);
      toast.success(`Reverted match for invoice ${invoice.invoice_number}`);
      fetchInvoices();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const formatCurrencyMap = (map, targetCurrency) => {
    const entries = Object.entries(map).filter(([, v]) => v > 0);
    if (entries.length === 0) return null;
    if (targetCurrency && ratesReady) {
      const total = convertAmountsToTarget(map, targetCurrency, fxRatesMap);
      return total > 0 ? formatCurrency(total, targetCurrency) : null;
    }
    if (targetCurrency && entries.length === 1 && entries[0][0] === targetCurrency) {
      // Single native currency already matches the target — no conversion needed,
      // so this is exact even before live rates have loaded.
      return formatCurrency(entries[0][1], targetCurrency);
    }
    if (targetCurrency) return null; // rates not loaded yet and a real conversion is required
    return entries.map(([cur, amt]) => formatCurrency(amt, cur)).join(' · ');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Invoices & Receipts</h1>
            <p className="text-muted-foreground mt-1">Manage supplier and client invoices</p>
          </div>
        </div>

        {/* Section tabs (Invoices / Review Queue) */}
        <div
          className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-fit"
          data-testid="invoices-section-tabs"
        >
          {INVOICES_SECTION_TABS.map(function (tab) {
            return (
              <button
                key={tab.key}
                onClick={function () { handleSectionChange(tab.key); }}
                className={cn(
                  'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all',
                  section === tab.key
                    ? 'bg-background text-foreground shadow'
                    : 'hover:bg-background/50 hover:text-foreground'
                )}
                data-testid={'invoices-section-tab-' + tab.key}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Review Queue section */}
        {section === 'review' && <ReviewQueueContent />}

        {/* Invoices section */}
        {section !== 'review' && (<>
        <div className="flex items-center justify-end gap-2">
            {invoiceTab === 'client' && (
              <Button
                variant="outline"
                onClick={handleImportBros}
                disabled={isImportingBros}
                data-testid="import-bros-btn"
              >
                {isImportingBros ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {isImportingBros ? 'Importing...' : 'Import BROS'}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={isScanning}
                  data-testid="scan-gmail-btn"
                >
                  {isScanning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  {isScanning ? 'Scanning...' : 'Scan Gmail'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleScanGmail(false)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Scan new emails
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleScanGmail(true)}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Force rescan all
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              onClick={handleSyncReceipts}
              disabled={isSyncingReceipts}
              data-testid="sync-receipts-btn"
            >
              {isSyncingReceipts ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {isSyncingReceipts ? 'Syncing...' : 'Sync Receipts'}
            </Button>
            <Button data-testid="upload-invoice-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Invoice
            </Button>
        </div>

        {/* Supplier / Client tabs */}
        <Tabs value={invoiceTab} onValueChange={(val) => {
          setInvoiceTab(val);
          setDirectionFilter(val === 'supplier' ? 'inbound' : 'outbound');
          // Taxonomy buckets differ by direction (supplier vs client) --
          // a chip selected on one tab has no meaning on the other.
          setTaxonomyFilter('all');
        }} className="-mt-2">
          <TabsList>
            <TabsTrigger value="client">Client Invoices</TabsTrigger>
            <TabsTrigger value="supplier">Supplier Invoices</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Stats */}
        <div className="flex items-center justify-end">
          <CurrencyToggle />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total</p>
              <div className="mt-0.5">{renderAmounts(stats.total.amounts)}</div>
              <p className="text-xs text-muted-foreground/60 mt-1">
                {stats.total.count} invoices
                {stats.total.excludedCount > 0 && (
                  <span data-testid="invoices-unparseable-excluded-count">
                    {' '}({stats.total.excludedCount} excluded)
                  </span>
                )}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Unpaid</p>
              <div className="mt-0.5">{renderAmounts(stats.unpaid.amounts, 'text-yellow-600 dark:text-yellow-500')}</div>
              <p className="text-xs text-muted-foreground/60 mt-1">{stats.unpaid.count} invoices</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Overdue</p>
              <div className="mt-0.5">{renderAmounts(stats.overdue.amounts, 'text-red-600 dark:text-red-500')}</div>
              <p className="text-xs text-muted-foreground/60 mt-1">{stats.overdue.count} invoices · unpaid &amp; past due</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Paid</p>
              <div className="mt-0.5">{renderAmounts(stats.paid.amounts, 'text-green-600 dark:text-green-500')}</div>
              <p className="text-xs text-muted-foreground/60 mt-1">{stats.paid.count} invoices</p>
            </CardContent>
          </Card>
        </div>

        <p className="text-xs text-muted-foreground/50 -mt-4 text-right">
          Converted to {displayCurrency} · approx., live rate
        </p>

        {/* Jurisdiction Breakdowns — shown in aggregate view */}
        {selectedEntity === 'all' && invoices.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {jurisdictions.map((jur) => {
              const jurData = byJurisdiction[jur.key] || { amounts: {}, count: 0 };
              const tc = jur.currency;
              const totalStr = formatCurrencyMap(jurData.amounts, tc);
              const childEntities = Object.values(byEntityBreakdown).filter(
                ({ entity }) => entity.jurisdiction === jur.key
              );
              return (
                <motion.div
                  key={jur.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                >
                  <Card
                    className="h-full overflow-hidden transition-shadow hover:shadow-xl"
                    style={{ borderTop: `3px solid ${jur.color}` }}
                  >
                    <CardContent className="p-6 space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl leading-none">{jur.flag}</span>
                          <div>
                            <h3 className="font-semibold text-base leading-tight">{jur.name}</h3>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {jurData.count > 0 ? `${jurData.count} invoices` : 'No invoices'}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className="font-mono text-xs shrink-0">
                          {jur.currency}
                        </Badge>
                      </div>

                      {/* Total */}
                      {jurData.count > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground">Total (approx., live rate)</p>
                          <p className="text-2xl font-bold font-numeric">
                            {totalStr ?? '—'}
                          </p>
                        </div>
                      )}

                      {/* Entity breakdown */}
                      {childEntities.length > 0 && (
                        <div className="border-t pt-2">
                          <table className="w-full text-xs">
                            <thead>
                              <tr>
                                <th className="text-left pb-1.5 font-normal text-muted-foreground/70">Entity</th>
                                <th className="text-right pb-1.5 font-normal text-muted-foreground/70">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {childEntities.map(({ entity, amounts, count }) => {
                                const amtStr = formatCurrencyMap(amounts, tc);
                                const dotColor = getEntityDotColor(entity.name, entity.type);
                                return (
                                  <tr key={entity.id} className="border-t border-border/30">
                                    <td className="py-1.5">
                                      <span className="flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                                        <span className="font-medium">{entity.name}</span>
                                        <span className="tabular-nums text-muted-foreground/50">{count}</span>
                                      </span>
                                    </td>
                                    <td className="py-1.5 text-right font-numeric text-muted-foreground">
                                      {amtStr ?? <span className="text-muted-foreground/30">—</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="invoice-search-input"
                />
              </div>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="entity-filter">
                  <SelectValue placeholder="Entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="eci">Edinburgh Capital Investments</SelectItem>
                  <SelectItem value="br_ltd">Bonjour Residences</SelectItem>
                  <SelectItem value="hsr">High Street Residence</SelectItem>
                  <SelectItem value="bi_ltd">Bonjour Investments Ltd</SelectItem>
                  <SelectItem value="bi_sas">Bonjour Investments SAS</SelectItem>
                  <SelectItem value="bi_uk">Bonjour Invest UK</SelectItem>
                  <SelectItem value="bh">Bonjour Holding</SelectItem>
                  <SelectItem value="afpv">AFPV Invest</SelectItem>
                  <SelectItem value="bi_immo">Bonjour Immobilier</SelectItem>
                  <SelectItem value="sci_113">SCI 113 Rue Saint-Honoré</SelectItem>
                  <SelectItem value="pfv">PFV Holding</SelectItem>
                  <SelectItem value="fan_zhang">Fan Zhang</SelectItem>
                  <SelectItem value="fan_zhang_piotr_kacki">Fan Zhang and Piotr Kacki</SelectItem>
                  <SelectItem value="piotr_kacki">Piotr Kacki</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-full sm:w-[160px]" data-testid="source-filter">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="gmail">Gmail</SelectItem>
                  <SelectItem value="pennylane">Pennylane</SelectItem>
                  <SelectItem value="bros">BROS</SelectItem>
                  <SelectItem value="chief_of_staff">Chief of Staff</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="management_statement">Management statement</SelectItem>
                </SelectContent>
              </Select>
              <DateScopeControl value={dateScope} onChange={setDateScope} defaultFrom={DEFAULT_DATE_SCOPE.from} />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="status-filter">
                  <SelectValue placeholder="Payment Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payment Statuses</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="partially_paid">Partially Paid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <Select value={reconFilter} onValueChange={setReconFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="recon-filter">
                  <SelectValue placeholder="Reconciliation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Recon Status</SelectItem>
                  <SelectItem value="reconciled">Reconciled</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="not_reconciled">Not Reconciled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={docTypeFilter} onValueChange={setDocTypeFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="doctype-filter">
                  <SelectValue placeholder="Document Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doc Types</SelectItem>
                  <SelectItem value="invoice">Invoices</SelectItem>
                  <SelectItem value="government">Government & Tax</SelectItem>
                  <SelectItem value="receipt">Receipts</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {invoiceTab === 'supplier' && (
          <TaxonomyChipRow
            categories={SUPPLIER_TAXONOMY_CATEGORIES}
            value={taxonomyFilter}
            onChange={setTaxonomyFilter}
            counts={taxonomyCounts}
          />
        )}
        {invoiceTab === 'client' && (
          <TaxonomyChipRow
            categories={CLIENT_TAXONOMY_CATEGORIES}
            value={taxonomyFilter}
            onChange={setTaxonomyFilter}
            counts={taxonomyCounts}
          />
        )}
        {/* Country filter: same chip-row UI as Taxonomy, on both tabs --
            derived server-side from each doc's entity (config.py
            resolve_entity_country), never a client-side re-filter. */}
        <TaxonomyChipRow
          categories={COUNTRY_CATEGORIES}
          value={countryFilter}
          onChange={setCountryFilter}
          counts={countryCounts}
        />

        {/* Invoice table -- one table + the chip row is the whole UI (CLAUDE.md
            Commit 3.2: the old "Supplier Invoices & Receipts" / "Government &
            Tax" grouped boxes were a redundant second layer of filtering on
            top of the taxonomy chips and Doc Type dropdown, which already
            cover this). */}
        {(() => {
          const renderInvoiceTable = (items, emptyMessage) => (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Country</th>
                    <th>{invoiceTab === 'client' ? 'Client' : 'Vendor'}</th>
                    <th>Entity</th>
                    <th>Payer</th>
                    <th>Description</th>
                    <th>Due Date</th>
                    <th>Total</th>
                    <th>Excl. VAT</th>
                    <th>VAT</th>
                    <th>Category</th>
                    <th>Source</th>
                    <th>Payment</th>
                    <th
                      className="cursor-pointer select-none hover:text-foreground"
                      onClick={() => toggleSort('payment_date')}
                      data-testid="invoices-th-payment-date"
                    >
                      Payment Date{sortBy === 'payment_date' ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                    </th>
                    <th>Reconciled</th>
                    <th>Accounting</th>
                    <th>Invoice #</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((invoice, index) => {
                    // entity_id is the type code (eci, br_ltd, etc.), not a UUID
                    const entityType = invoice.entity_id;
                    const rawEntityName = invoice.entity_name || entityType;
                    const entityDisplayName = (rawEntityName && ENTITY_TYPE_DISPLAY_NAME[rawEntityName]) || rawEntityName;
                    const entity = entityType ? { id: entityType, name: entityDisplayName, type: entityType } : null;
                    // Stored directly on the invoice by routes/matching.py's
                    // shared payment-stamp writer (_payer_stamp_fields) --
                    // no live Match-collection lookup needed, it can never
                    // drift from what the writer actually stamped.
                    const crossPayerEntityId = invoice.payer_entity_id;
                    const crossPayerEntity = crossPayerEntityId ? { id: crossPayerEntityId, name: invoice.payer_entity_name || crossPayerEntityId, type: crossPayerEntityId } : null;
                    const isCrossEntity = crossPayerEntity && crossPayerEntityId !== invoice.entity_id;
                    const supplierClient = invoice.supplier_client_name || 'Unknown';
                    const invoiceSource = invoice.source || 'manual';
                    const srcCfg = sourceConfig[invoiceSource] ?? sourceConfig.manual;
                    const overdue = isOverdue(invoice);
                    const payCfg = paymentStatusConfig[invoice.payment_status] ?? paymentStatusConfig.unpaid;
                    const reconCfg = reconStatusConfig[invoice.recon_status] ?? reconciledConfig[invoice.reconciled] ?? reconStatusConfig.not_reconciled;
                    const PayIcon = payCfg.icon;
                    const reviewBadge = reviewStatusBadge(invoice);
                    const dupBadge = duplicateNotificationBadge(invoice);
                    const groupMatchId = groupIdByInvoiceId[invoice.id];
                    const groupMatchCount = groupMatchId
                      ? confirmedMatches.filter((m) => m.match_group_id === groupMatchId).length
                      : 0;
                    return (
                      <motion.tr
                        key={invoice.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className="hover:bg-muted/50"
                        data-testid={`invoice-row-${invoice.id}`}
                      >
                        {/* Date */}
                        <td className="text-muted-foreground">
                          {safeFormatDate(invoice.date)}
                        </td>
                        {/* Country */}
                        <td>
                          {(() => {
                            const countryFlags = { GB: '🇬🇧', FR: '🇫🇷', DE: '🇩🇪', ES: '🇪🇸', IT: '🇮🇹', NL: '🇳🇱', DK: '🇩🇰', US: '🇺🇸', CH: '🇨🇭', SE: '🇸🇪', NO: '🇳🇴', BE: '🇧🇪', IE: '🇮🇪', AT: '🇦🇹', PT: '🇵🇹' };
                            const countryNames = { GB: 'United Kingdom', FR: 'France', DE: 'Germany', ES: 'Spain', IT: 'Italy', NL: 'Netherlands', DK: 'Denmark', US: 'United States', CH: 'Switzerland', SE: 'Sweden', NO: 'Norway', BE: 'Belgium', IE: 'Ireland', AT: 'Austria', PT: 'Portugal' };
                            const isoCode = (invoice.country || '').toUpperCase().trim();
                            if (countryFlags[isoCode]) return <span className="text-sm" title={countryNames[isoCode]}>{countryFlags[isoCode]}</span>;
                            const jurKey = entity && getJurisdictionKey(entity.name, entity.type);
                            const jur = jurKey && jurisdictionsByKey[jurKey];
                            if (jur) return <span className="text-sm" title={jur.name}>{jur.flag}</span>;
                            return <span className="text-muted-foreground text-sm">—</span>;
                          })()}
                        </td>
                        {/* Vendor */}
                        <td className="font-medium">{supplierClient}</td>
                        {/* Entity */}
                        <td>
                          {entity ? (
                            <div className="flex items-center gap-1.5">
                              <span
                                className="w-2 h-2 rounded-full shrink-0"
                                style={{ backgroundColor: getEntityDotColor(entity.name, entity.type) }}
                              />
                              <span className="text-sm font-medium">{entity.name}</span>
                              {invoice.entity_id_source === 'inferred_from_bank_match' && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1 py-0"
                                  data-testid={`invoice-entity-inferred-${invoice.id}`}
                                  title="Entity was unassigned; resolved from the single matching bank transaction, not chosen by a human."
                                >
                                  inferred
                                </Badge>
                              )}
                            </div>
                          ) : invoice.entity_name ? (
                            <span className="text-sm font-medium">{invoice.entity_name}</span>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </td>
                        {/* Payer — renders whenever payer_entity_id is stamped
                            (paid_at and payer are always populated together, see
                            routes/matching.py::_payer_stamp_fields); a same-entity
                            payer is NOT hidden, only a cross-entity one is flagged. */}
                        <td>
                          {crossPayerEntity ? (
                            <div className="flex items-center gap-1.5">
                              <span
                                className="w-2 h-2 rounded-full shrink-0"
                                style={{ backgroundColor: getEntityDotColor(crossPayerEntity.name, crossPayerEntity.type) }}
                              />
                              <span className="text-sm font-medium">{crossPayerEntity.name}</span>
                              {isCrossEntity && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] px-1 py-0"
                                  data-testid={`invoice-payer-cross-entity-${invoice.id}`}
                                  title="Paid from a different entity's account than this invoice's own entity."
                                >
                                  cross-entity
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </td>
                        {/* Description */}
                        <td
                          className="text-sm text-muted-foreground max-w-[200px] truncate cursor-pointer hover:text-foreground"
                          onClick={() => (invoice.description || invoice.email_subject) && setDescriptionInvoice(invoice)}
                        >
                          {invoice.description || invoice.email_subject || <span className="text-muted-foreground/40">—</span>}
                        </td>
                        {/* Due Date */}
                        <td className={cn(overdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-muted-foreground')}>
                          {safeFormatDate(invoice.due_date)}
                          {overdue && <span className="ml-1 text-xs">(overdue)</span>}
                        </td>
                        {/* Total */}
                        <td className="font-numeric font-medium">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </td>
                        {/* Excl. VAT */}
                        <td className="font-numeric text-muted-foreground text-sm">
                          {invoice.amount_excl_vat
                            ? formatCurrency(invoice.amount_excl_vat, invoice.currency)
                            : invoice.vat_amount
                              ? formatCurrency(invoice.amount - invoice.vat_amount, invoice.currency)
                              : '—'}
                        </td>
                        {/* VAT */}
                        <td className="font-numeric text-muted-foreground text-sm">
                          {invoice.vat_amount
                            ? formatCurrency(invoice.vat_amount, invoice.currency)
                            : '—'}
                        </td>
                        {/* Category */}
                        <td>
                          {(() => {
                            const categoryLabels = {
                              subscription: 'Subscription',
                              operational: 'Operational',
                              utilities: 'Utilities',
                              government: 'Government',
                              debt_recovery: 'Debt Recovery',
                              supplier: 'Supplier',
                              management_statement: 'Mgmt statement',
                            };
                            const categoryColors = {
                              subscription: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
                              operational: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
                              utilities: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
                              government: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
                              debt_recovery: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
                              supplier: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300',
                              management_statement: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
                            };
                            // `category` is a COST category; a client invoice
                            // has none, and defaulting to 'supplier' printed a
                            // literal "Supplier" badge on every BR OS client
                            // invoice (Fan, 2026-08-30).
                            const cat = invoice.category || (invoice.type === 'outbound' ? null : 'supplier');
                            if (!cat) return <span className="text-muted-foreground">—</span>;
                            return (
                              <Badge variant="secondary" className={cn('text-xs', categoryColors[cat])}>
                                {categoryLabels[cat] || cat}
                              </Badge>
                            );
                          })()}
                        </td>
                        {/* Source */}
                        <td>
                          <Badge variant="secondary" className={cn('text-xs', srcCfg.className)}>
                            {srcCfg.label}
                          </Badge>
                        </td>
                        {/* Payment Status -- reviewBadge (Quarantined /
                            Pending review / Rejected / Unparseable amount)
                            is additional, never a substitute: a quarantined
                            row must never render as a plain Unpaid invoice
                            again. */}
                        <td>
                          <div className="flex flex-col items-start gap-1">
                            <Badge className={payCfg.className}>
                              <PayIcon className="h-3 w-3 mr-1" />
                              {payCfg.label}
                            </Badge>
                            {reviewBadge && (
                              <Badge
                                data-testid={`invoice-review-badge-${invoice.id}`}
                                className={cn('text-xs', reviewBadge.className)}
                              >
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {reviewBadge.label}
                              </Badge>
                            )}
                            {dupBadge && (
                              <Badge
                                data-testid={`invoice-duplicate-badge-${invoice.id}`}
                                className={cn('text-xs', dupBadge.className)}
                              >
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {dupBadge.label}
                              </Badge>
                            )}
                            {groupMatchId && (
                              <Badge
                                data-testid={`invoice-group-match-badge-${invoice.id}`}
                                variant="secondary"
                                className="text-xs"
                                title={`Settled together with ${groupMatchCount - 1} other invoice(s) by one transaction`}
                              >
                                <Layers className="h-3 w-3 mr-1" />
                                Group ({groupMatchCount})
                              </Badge>
                            )}
                          </div>
                        </td>
                        {/* Payment Date -- paid_at, the settling
                            transaction's own date, stamped by the same
                            writer as payer/payment_source. Empty for an
                            unreconciled row, never a placeholder. */}
                        <td className="text-muted-foreground" data-testid={`invoice-payment-date-${invoice.id}`}>
                          {invoice.payment_date ? safeFormatDate(invoice.payment_date) : '—'}
                        </td>
                        {/* Reconciled */}
                        <td>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="secondary" className={cn('text-xs', reconCfg.className)}>
                              {reconCfg.label}
                            </Badge>
                            {confirmedInvoiceIds.has(invoice.id) && (
                              <button
                                className="text-xs text-muted-foreground hover:text-destructive underline underline-offset-2"
                                title="Revert this confirmed match"
                                data-testid={`revert-match-${invoice.id}`}
                                onClick={() => handleRevertMatch(invoice)}
                              >
                                Revert
                              </button>
                            )}
                          </div>
                        </td>
                        {/* Accounting Sync */}
                        <td>
                          {(invoice.source === 'pennylane' || invoice.source === 'xero') ? (
                            <Badge variant="secondary" className={cn('text-xs',
                              invoice.accounting_synced === 'synced' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                              invoice.accounting_synced === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                              invoice.accounting_synced === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                              'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                            )}>
                              {invoice.accounting_synced === 'synced' ? 'Synced' :
                               invoice.accounting_synced === 'failed' ? 'Failed' :
                               invoice.accounting_synced === 'pending' ? 'Pending' : '—'}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground/40 text-xs" title="No Finance-Agent accounting push exists for this source by design">N/A</span>
                          )}
                        </td>
                        {/* Invoice # / Link */}
                        <td>
                          <button
                            className="flex items-center gap-1.5 font-numeric text-sm text-primary hover:underline underline-offset-2 cursor-pointer"
                            onClick={() => setSelectedInvoice(invoice)}
                          >
                            {invoice.invoice_number}
                            <InvoicePdfButton invoice={invoice} pdfMatchInfo={pdfMatchMap[invoice.id]} variant="icon" />
                          </button>
                        </td>
                        <td>
                          {/* Management-statement rows are display/worklist only --
                              they aren't a db.invoices document, so edit/delete/set-
                              category/set-entity (which all write to an invoice by
                              id) don't apply. They reconcile via the statement
                              payout matcher, not these row actions. */}
                          {invoice.source === 'management_statement' ? (
                            <span className="text-muted-foreground/40 text-xs pl-2">—</span>
                          ) : (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                  title="Set category"
                                >
                                  <Tag className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {(invoiceTab === 'supplier' ? SUPPLIER_TAXONOMY_CATEGORIES : CLIENT_TAXONOMY_CATEGORIES)
                                  .filter((c) => c.key !== 'unclassified' && c.key !== 'management_statements').map((c) => (
                                    <DropdownMenuItem
                                      key={c.key}
                                      onClick={() => handleSetCategory(invoice, c.key, c.label)}
                                    >
                                      {c.label}
                                    </DropdownMenuItem>
                                  ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            {invoice.entity_id === 'unassigned' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-amber-600 hover:text-amber-700"
                                    title="Set entity"
                                    data-testid={`set-entity-${invoice.id}`}
                                  >
                                    <Building2 className="h-3.5 w-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {assignableEntities.map((e) => (
                                    <DropdownMenuItem
                                      key={e.id}
                                      onClick={() => handleSetEntity(invoice, e.id, e.name)}
                                    >
                                      {e.name}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              title="Change status"
                              onClick={() => openEditDialog(invoice)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              title="Delete invoice"
                              onClick={() => setDeletingInvoice(invoice)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );

          if (isLoading) {
            return (
              <Card>
                <CardContent className="p-0">
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                </CardContent>
              </Card>
            );
          }

          const brosList = sortedInvoices.filter(inv => inv.source === 'bros');
          const brosPdfCount = brosList.filter(inv => inv.xero_invoice_id || pdfMatchMap[inv.id]?.matched).length;
          const emptyMessage = docTypeFilter === 'all'
            ? 'No invoices found'
            : `No ${docTypeFilter === 'government' ? 'government & tax' : docTypeFilter + 's'} found`;

          return (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{invoiceTab === 'client' ? 'Client Invoices' : 'Supplier Invoices'}</CardTitle>
                <CardDescription>
                  {sortedInvoices.length} items
                  {brosList.length > 0 && (
                    <span className="ml-2 text-muted-foreground/70">
                      · {brosPdfCount} of {brosList.length} BROS have PDF
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {sortedInvoices.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground text-sm">{emptyMessage}</p>
                  </div>
                ) : renderInvoiceTable(sortedInvoices)}
              </CardContent>
            </Card>
          );
        })()}

        {/* Pagination -- server-side, replaces the old flat 500-row cap so a
            filter matching thousands of docs (e.g. a taxonomy chip) stays
            browsable to the end instead of silently truncating. */}
        {totalInvoiceCount > 0 && (() => {
          const totalPages = Math.max(1, Math.ceil(totalInvoiceCount / PAGE_SIZE));
          return (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, totalInvoiceCount)}{'–'}{Math.min(page * PAGE_SIZE, totalInvoiceCount)} of {totalInvoiceCount.toLocaleString()} invoices
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  {page} / {totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          );
        })()}

        <InvoiceDetailModal
          invoice={selectedInvoice}
          isOpen={!!selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          pdfMatchInfo={selectedInvoice ? pdfMatchMap[selectedInvoice.id] : undefined}
        />

        {/* Edit Invoice Dialog */}
        <Dialog open={!!editingInvoice} onOpenChange={() => setEditingInvoice(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Invoice</DialogTitle>
              <DialogDescription>
                {editingInvoice?.invoice_number || 'New'} &mdash; {editingInvoice?.supplier_client_name || 'Unknown'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {/* Row 1: Vendor + Invoice # */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-vendor">Vendor / Supplier</Label>
                  <Input id="edit-vendor" value={editForm.supplier_client_name || ''} onChange={(e) => setEditForm(f => ({ ...f, supplier_client_name: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-invnum">Invoice #</Label>
                  <Input id="edit-invnum" value={editForm.invoice_number || ''} onChange={(e) => setEditForm(f => ({ ...f, invoice_number: e.target.value }))} />
                </div>
              </div>

              {/* Row 2: Entity + Payer */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Entity</Label>
                  <Select value={editForm.entity_name || ''} onValueChange={(val) => {
                    const entityMap = {
                      'AFPV Invest': 'afpv', 'Bonjour Immobilier': 'bi_immo', 'SCI 113 Rue Saint-Honoré': 'sci_113',
                      'PFV Holding': 'pfv', 'Bonjour Investments SAS': 'bi_sas',
                      'Personal': 'fan_zhang_piotr_kacki', 'Fan Zhang and Piotr Kacki': 'fan_zhang_piotr_kacki', 'Fan Zhang & Piotr Kacki': 'fan_zhang_piotr_kacki', 'Fan Zhang': 'fan_zhang', 'Piotr Kacki': 'piotr_kacki',
                      'Bonjour Residences': 'br_ltd', 'Bonjour Investments Ltd': 'bi_ltd', 'Bonjour Invest UK': 'bi_uk',
                      'Edinburgh Capital Investments': 'eci', 'High Street Residence': 'hsr', 'Bonjour Holding': 'bh',
                    };
                    setEditForm(f => ({ ...f, entity_name: val, entity_id: entityMap[val] || f.entity_id }));
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select entity" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AFPV Invest">AFPV Invest</SelectItem>
                      <SelectItem value="Bonjour Immobilier">Bonjour Immobilier</SelectItem>
                      <SelectItem value="SCI 113 Rue Saint-Honoré">SCI 113 Rue Saint-Honoré</SelectItem>
                      <SelectItem value="PFV Holding">PFV Holding</SelectItem>
                      <SelectItem value="Bonjour Investments SAS">Bonjour Investments SAS</SelectItem>
                      <SelectItem value="Bonjour Residences">Bonjour Residences</SelectItem>
                      <SelectItem value="Bonjour Investments Ltd">Bonjour Investments Ltd</SelectItem>
                      <SelectItem value="Bonjour Invest UK">Bonjour Invest UK</SelectItem>
                      <SelectItem value="Edinburgh Capital Investments">Edinburgh Capital Investments</SelectItem>
                      <SelectItem value="High Street Residence">High Street Residence</SelectItem>
                      <SelectItem value="Bonjour Holding">Bonjour Holding</SelectItem>
                      <SelectItem value="Personal">Personal</SelectItem>
                      <SelectItem value="Fan Zhang and Piotr Kacki">Fan Zhang and Piotr Kacki</SelectItem>
                      <SelectItem value="Fan Zhang">Fan Zhang</SelectItem>
                      <SelectItem value="Piotr Kacki">Piotr Kacki</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Payer</Label>
                  <Select value={editForm.payer_entity_name || '_none'} onValueChange={(val) => {
                    if (val === '_none') {
                      setEditForm(f => ({ ...f, payer_entity_name: '', payer_entity_id: '' }));
                    } else {
                      const entityMap = {
                        'AFPV Invest': 'afpv', 'Bonjour Immobilier': 'bi_immo', 'SCI 113 Rue Saint-Honoré': 'sci_113',
                        'PFV Holding': 'pfv', 'Bonjour Investments SAS': 'bi_sas',
                        'Personal': 'fan_zhang_piotr_kacki', 'Fan Zhang and Piotr Kacki': 'fan_zhang_piotr_kacki', 'Fan Zhang & Piotr Kacki': 'fan_zhang_piotr_kacki', 'Fan Zhang': 'fan_zhang', 'Piotr Kacki': 'piotr_kacki',
                        'Bonjour Residences': 'br_ltd', 'Bonjour Investments Ltd': 'bi_ltd', 'Bonjour Invest UK': 'bi_uk',
                        'Edinburgh Capital Investments': 'eci', 'High Street Residence': 'hsr', 'Bonjour Holding': 'bh',
                      };
                      setEditForm(f => ({ ...f, payer_entity_name: val, payer_entity_id: entityMap[val] || '' }));
                    }
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select payer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— None —</SelectItem>
                      <SelectItem value="AFPV Invest">AFPV Invest</SelectItem>
                      <SelectItem value="Bonjour Immobilier">Bonjour Immobilier</SelectItem>
                      <SelectItem value="SCI 113 Rue Saint-Honoré">SCI 113 Rue Saint-Honoré</SelectItem>
                      <SelectItem value="PFV Holding">PFV Holding</SelectItem>
                      <SelectItem value="Bonjour Investments SAS">Bonjour Investments SAS</SelectItem>
                      <SelectItem value="Bonjour Residences">Bonjour Residences</SelectItem>
                      <SelectItem value="Bonjour Investments Ltd">Bonjour Investments Ltd</SelectItem>
                      <SelectItem value="Bonjour Invest UK">Bonjour Invest UK</SelectItem>
                      <SelectItem value="Edinburgh Capital Investments">Edinburgh Capital Investments</SelectItem>
                      <SelectItem value="High Street Residence">High Street Residence</SelectItem>
                      <SelectItem value="Bonjour Holding">Bonjour Holding</SelectItem>
                      <SelectItem value="Personal">Personal</SelectItem>
                      <SelectItem value="Fan Zhang & Piotr Kacki">Fan Zhang & Piotr Kacki</SelectItem>
                      <SelectItem value="Fan Zhang">Fan Zhang</SelectItem>
                      <SelectItem value="Piotr Kacki">Piotr Kacki</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 3: Description */}
              <div className="space-y-1.5">
                <Label htmlFor="edit-desc">Description</Label>
                <Input id="edit-desc" value={editForm.description || ''} onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))} />
              </div>

              {/* Row 4: Amount + Excl VAT + VAT + VAT Rate */}
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-amount">Total Amount</Label>
                  <Input id="edit-amount" type="number" step="0.01" value={editForm.amount ?? ''} onChange={(e) => setEditForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-excl">Excl. VAT</Label>
                  <Input id="edit-excl" type="number" step="0.01" value={editForm.amount_excl_vat ?? ''} onChange={(e) => setEditForm(f => ({ ...f, amount_excl_vat: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-vat">VAT Amount</Label>
                  <Input id="edit-vat" type="number" step="0.01" value={editForm.vat_amount ?? ''} onChange={(e) => setEditForm(f => ({ ...f, vat_amount: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-vatrate">VAT Rate %</Label>
                  <Input id="edit-vatrate" type="number" step="0.1" value={editForm.vat_rate ?? ''} onChange={(e) => setEditForm(f => ({ ...f, vat_rate: e.target.value }))} />
                </div>
              </div>

              {/* Row 5: Currency + Country + Type */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <Select value={editForm.currency || 'GBP'} onValueChange={(val) => setEditForm(f => ({ ...f, currency: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="DKK">DKK</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-country">Country</Label>
                  <Select value={editForm.country || '_none'} onValueChange={(val) => setEditForm(f => ({ ...f, country: val === '_none' ? '' : val }))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">—</SelectItem>
                      <SelectItem value="GB">GB</SelectItem>
                      <SelectItem value="FR">FR</SelectItem>
                      <SelectItem value="DE">DE</SelectItem>
                      <SelectItem value="ES">ES</SelectItem>
                      <SelectItem value="IT">IT</SelectItem>
                      <SelectItem value="NL">NL</SelectItem>
                      <SelectItem value="DK">DK</SelectItem>
                      <SelectItem value="US">US</SelectItem>
                      <SelectItem value="CH">CH</SelectItem>
                      <SelectItem value="BE">BE</SelectItem>
                      <SelectItem value="IE">IE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select value={editForm.type || 'inbound'} onValueChange={(val) => setEditForm(f => ({ ...f, type: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inbound">Inbound (Supplier)</SelectItem>
                      <SelectItem value="outbound">Outbound (Client)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 6: Issue Date + Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-issuedate">Issue Date</Label>
                  <Input id="edit-issuedate" type="date" value={(editForm.issue_date || '').slice(0, 10)} onChange={(e) => setEditForm(f => ({ ...f, issue_date: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-duedate">Due Date</Label>
                  <Input id="edit-duedate" type="date" value={(editForm.due_date || '').slice(0, 10)} onChange={(e) => setEditForm(f => ({ ...f, due_date: e.target.value }))} />
                </div>
              </div>

              {/* Row 7: Payment Status + Category + Document Type */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Payment Status</Label>
                  <Select value={editForm.payment_status || 'unpaid'} onValueChange={(val) => setEditForm(f => ({ ...f, payment_status: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(paymentStatusConfig).map(([value, cfg]) => (
                        <SelectItem key={value} value={value}>
                          <div className="flex items-center gap-2">
                            <cfg.icon className="h-3.5 w-3.5" />
                            {cfg.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Select value={editForm.category || 'supplier'} onValueChange={(val) => setEditForm(f => ({ ...f, category: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="subscription">Subscription</SelectItem>
                      <SelectItem value="operational">Operational</SelectItem>
                      <SelectItem value="utilities">Utilities</SelectItem>
                      <SelectItem value="government">Government</SelectItem>
                      <SelectItem value="debt_recovery">Debt Recovery</SelectItem>
                      <SelectItem value="supplier">Supplier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Document Type</Label>
                  <Select value={editForm.document_type || 'invoice'} onValueChange={(val) => setEditForm(f => ({ ...f, document_type: val }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="receipt">Receipt</SelectItem>
                      <SelectItem value="government">Government</SelectItem>
                      <SelectItem value="credit_note">Credit Note</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Reconciliation status is managed automatically by the reconciliation engine.
              </p>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingInvoice(null)}>Cancel</Button>
                <Button onClick={handleSaveInvoice} disabled={isSaving}>
                  {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deletingInvoice} onOpenChange={() => setDeletingInvoice(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deletingInvoice?.invoice_number}</strong> from{' '}
                <strong>{deletingInvoice?.supplier_client_name || 'Unknown'}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => handleDelete(deletingInvoice.id)}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Description / Email Popup */}
        <Dialog open={!!descriptionInvoice} onOpenChange={() => setDescriptionInvoice(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                {descriptionInvoice?.supplier_client_name || 'Invoice Details'}
              </DialogTitle>
              {descriptionInvoice?.invoice_number && (
                <DialogDescription>Invoice #{descriptionInvoice.invoice_number}</DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                {descriptionInvoice?.email_subject && (
                  <>
                    <span className="font-medium text-muted-foreground">Subject</span>
                    <span>{descriptionInvoice.email_subject}</span>
                  </>
                )}
                {descriptionInvoice?.email_from && (
                  <>
                    <span className="font-medium text-muted-foreground">From</span>
                    <span>{descriptionInvoice.email_from}</span>
                  </>
                )}
                {descriptionInvoice?.email_to && (
                  <>
                    <span className="font-medium text-muted-foreground">To</span>
                    <span>{descriptionInvoice.email_to}</span>
                  </>
                )}
                {descriptionInvoice?.email_date && (
                  <>
                    <span className="font-medium text-muted-foreground">Date</span>
                    <span>{descriptionInvoice.email_date}</span>
                  </>
                )}
              </div>
              {descriptionInvoice?.email_body ? (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Email Body</p>
                  <div className="text-sm whitespace-pre-wrap bg-muted/50 rounded-md p-4 max-h-[40vh] overflow-y-auto border">
                    {descriptionInvoice.email_body}
                  </div>
                </div>
              ) : descriptionInvoice?.description && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Description</p>
                  <p className="text-sm whitespace-pre-wrap">{descriptionInvoice.description}</p>
                </div>
              )}
              <div className="flex items-center justify-between text-sm pt-2 border-t">
                <span className="text-muted-foreground">
                  {descriptionInvoice?.amount != null && (
                    <>
                      {descriptionInvoice.currency || 'EUR'} {Number(descriptionInvoice.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </>
                  )}
                </span>
                {(descriptionInvoice?.file_url || descriptionInvoice?.media_url) && (
                  <a
                    href={descriptionInvoice.file_url || descriptionInvoice.media_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View Document
                  </a>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </>)}
      </div>
    </DashboardLayout>
  );
}
